import {AxiosError} from "axios";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {CAApi, X509CertificateDetails} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const caApi = new CAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface FinalizePendingRequestDialogProps {
    onClose : OnClose
}

export const FinalizePendingRequestDialog = ({onClose} : FinalizePendingRequestDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The files to import
     */
    const [file, setFile] = useState<File>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const importCertificates = () =>
    {
        setImporting(true);

        caApi.cAControllerFinalizePendingRequest(file)
            .then((response ) =>
            {
                const imported : X509CertificateDetails[] = response.data;

                if (imported.length > 0) {
                    onClose(DialogCloseCause.Succes);
                }
                else {
                    setErrorMessage("There were no matching CSRs");
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error finalizing certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setImporting(false);
            });
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("ca.finalize-csr")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t("ca.certificate-file")}</Form.Label>
                        <Form.Control
                            size="sm"
                            type="file"
                            accept=""
                            onChange={e => setFile(((e.target as HTMLInputElement).files as FileList)[0])}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importCertificates}
                        disabled={file === undefined}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("common.importing") : t("common.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
